import { Loader2, MessageSquarePlus, Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import type { ChatConversationSummary } from "./types";
import { relativeTime } from "./utils";

interface ConversationSidebarProps {
  /** Already scoped to one brand by the chat page — see `brand` below. */
  conversations: ChatConversationSummary[];
  activeId: string | null;
  /**
   * The brand the list is filtered to, purely for the empty-state copy. `null`
   * when there's no brand context yet (nothing has been filtered out).
   */
  brand: string | null;
  isLoading: boolean;
  onSelect: (id: string) => void;
  /** Opens the brand picker — creation itself happens there, not here. */
  onCreate: () => void;
  onDelete: (id: string) => void;
}

/** List of past conversations with a "New conversation" action. */
export function ConversationSidebar({
  conversations,
  activeId,
  brand,
  isLoading,
  onSelect,
  onCreate,
  onDelete,
}: ConversationSidebarProps) {
  return (
    <div className="flex h-full flex-col">
      <div className="border-border border-b p-3">
        <Button className="w-full justify-start gap-2" onClick={onCreate}>
          <MessageSquarePlus className="h-4 w-4" />
          New conversation
        </Button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto p-2">
        {isLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="text-muted h-5 w-5 animate-spin" />
          </div>
        ) : conversations.length === 0 ? (
          <p className="text-muted px-2 py-6 text-center text-xs">
            {brand ? `No ${brand} conversations yet.` : "No conversations yet."}
          </p>
        ) : (
          <ul className="space-y-1">
            {conversations.map((conversation) => {
              const isActive = conversation.id === activeId;
              return (
                <li key={conversation.id}>
                  <div
                    className={cn(
                      "group flex items-center gap-2 rounded-md px-2 py-2 text-sm",
                      isActive ? "bg-accent-light text-primary" : "hover:bg-primary/5",
                    )}
                  >
                    <button
                      type="button"
                      onClick={() => onSelect(conversation.id)}
                      className="min-w-0 flex-1 text-left"
                    >
                      {/* No brand badge here: the list only ever shows one
                          brand's conversations, so the brand lives once in the
                          header switcher instead of on every row. */}
                      <span className="text-foreground block truncate font-medium">
                        {conversation.title ?? "Untitled conversation"}
                      </span>
                      <span className="text-muted block truncate text-xs">
                        {conversation.machine?.model_name
                          ? `${conversation.machine.model_name} · ${relativeTime(conversation.updated_at)}`
                          : relativeTime(conversation.updated_at)}
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() => onDelete(conversation.id)}
                      className="text-muted hover:text-destructive shrink-0 opacity-0 transition-opacity group-hover:opacity-100"
                      aria-label="Delete conversation"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
